import { create } from "zustand";
import { api } from "../api";
import { layersFromDocument } from "../lib/layerLoad";
import {
  buildRxCauseComparison,
  RX_LEVEL_PPM,
  RX_MATERIAL_LBS,
  RX_SIMILAR_LBS,
  type RxComparison,
} from "../lib/rxCauseCompare";
import type { DashboardField, ManifestLayer } from "../types/contract";
import { captureRecSpec, fieldFailures, runRecBatch } from "../lib/fertBatch";
import { assembleFertLayers, withLimeSoilStamp } from "../lib/fertility";
import {
  limeFieldMethod,
  limeMethodBanner,
  limeTargetError,
  manifestHasBufferPh,
  pinPhTarget,
} from "../lib/limeBuffer";
import {
  resolveSeasons,
  DEFAULT_SEASON_CROPS,
  type SeasonPlan,
} from "../lib/replacement";
import {
  DEFAULT_TUNABLES,
  optsFor,
  replacementFor,
  seededBounds,
  withTunables,
  type Base,
  type RxTunables,
} from "../lib/rxInputs";
import {
  computePresetLayers,
  type FertPreset,
  type PresetInputs,
} from "../lib/fertPreset";
import {
  BOUNDARY_CHANGED,
  gridStamp,
  loadRxCells,
  type DisabledCovariateToggle,
} from "../lib/rxGrid";
import {
  solveCriticalForBudget,
  type BudgetSolution,
} from "../lib/budgetSolve";
import { budgetTarget, tunablesAfterBudget } from "../lib/rxBudget";
import { traditionalRxLayer } from "../lib/traditionalRx";
import { commitRecSet, type SavedRecSet } from "../lib/fertSets";
import { useFertRecs, type FertSummary } from "./fertRecsStore";

export type { RxTunables, SeasonPlan };
export { DEFAULT_TUNABLES, replacementFor, seededBounds, withTunables };
export { RX_LEVEL_PPM, RX_MATERIAL_LBS, RX_SIMILAR_LBS };
export type { RxComparison };

// The shape is deliberately the same as `zoneEditStore`: a session keyed to one field, live
// recompute on every tweak, and nothing persisted until THE ONE EXPENSIVE STEP HAPPENS ONCE.

let editSeq = 0;

interface RxEditState {
  /*
   *
   * THE CAUSE COMPARISON: categorize every cell by WHY traditional differs from genius — computed
   * EXACTLY, not statistically.
   */
  buildRxComparison: () => Promise<RxComparison | null>;
  buildTraditionalRx: () => Promise<ManifestLayer | null>;
  fieldId: string | null; // non-null = a session is live for this field
  fieldName: string;
  /* * fields selected when Customize opened; save applies one spec to each */
  batchFields: DashboardField[];
  // true once a multi-field save has run; a second Save would duplicate every set that succeeded
  saved: boolean;
  /* * for the field-total readout — growers order in pounds, not pounds per acre */
  fieldAcres: number | null;
  preset: FertPreset | null;
  albrecht: boolean;
  /* * which nutrient this session is editing — drives the whole panel and what gets saved */
  nutrient: "lime" | "k2o" | "p2o5";
  // WHICH CROPS each basis has on this field — not a blanket boolean. A boolean says "machine data
  // exists", which enables the button, but the map is keyed by CROP: pick a season crop the basis
  // has no layer for and the season contributes nothing, silently.
  yieldAvail: { machine: string[]; simulated: string[] };
  /*
   * what the last budget solve produced — surfaced so the panel shows the ACHIEVED average and names what
   * bound it, rather than echoing back the number the user typed
   */
  budget: BudgetSolution | null;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  skipped: string[];
  /** Covariate options /rx-inputs did not have. Each control stays disabled. */
  disabledToggles: DisabledCovariateToggle[];
  base: Base | null;
  tunables: RxTunables;
  /*
   * what the session opened with, AFTER field resolution — the baseline `reset` returns to and `dirty`
   * compares against. Using the static defaults for that would mark a freshly-opened editor as edited
   * and reset it to a rotation the field may not even have data for.
   */
  opened: RxTunables;
  layers: ManifestLayer[];
  summary: FertSummary | null;
  saving: boolean;
  /** Buildup lime on a field (or any batch field) that has an ml-bph map. */
  limeBufferActive: boolean;
  limeBatchBanner: string;
  limeTargetNotice: string | null;

  begin: (
    inputs: PresetInputs,
    preset: FertPreset,
    fieldName: string,
    fields?: DashboardField[],
  ) => Promise<void>;
  set: (patch: Partial<RxTunables>) => void;
  reset: () => void;
  save: (name: string) => Promise<{ ok: boolean; error?: string }>;
  end: () => void;
}

export const useRxEdit = create<RxEditState>((set, get) => ({
  fieldId: null,
  fieldName: "",
  batchFields: [],
  saved: false,
  fieldAcres: null,
  preset: null,
  albrecht: false,
  nutrient: "lime",
  budget: null,
  yieldAvail: { machine: [], simulated: [] },
  status: "idle",
  error: null,
  skipped: [],
  disabledToggles: [],
  base: null,
  tunables: { ...DEFAULT_TUNABLES },
  opened: { ...DEFAULT_TUNABLES },
  layers: [],
  summary: null,
  saving: false,
  limeBufferActive: false,
  limeBatchBanner: "",
  limeTargetNotice: null,

  begin: async (inputs, preset, fieldName, fields) => {
    const fieldId = inputs.field.field_id;
    const cohort = (fields?.length ? fields : [inputs.field]).slice();
    const nutrient: "lime" | "k2o" | "p2o5" =
      preset.nutrient === "k2o"
        ? "k2o"
        : preset.nutrient === "p2o5"
          ? "p2o5"
          : "lime";
    set({
      fieldId,
      fieldName,
      batchFields: cohort,
      saved: false,
      preset,
      nutrient,
      fieldAcres: inputs.field.acres ?? null,
      albrecht: !!(nutrient === "k2o"
        ? preset.flags.k2oAlbrecht
        : preset.flags.limeAlbrecht),
      status: "loading",
      error: null,
      skipped: [],
      disabledToggles: [],
      base: null,
      layers: [],
      yieldAvail: { machine: [], simulated: [] },
      budget: null,
      tunables: { ...DEFAULT_TUNABLES },
      opened: { ...DEFAULT_TUNABLES },
      limeBufferActive: false,
      limeBatchBanner: "",
      limeTargetNotice: null,
    });
    try {
      const limeUiPromise = resolveLimeBuffer(inputs, preset, cohort);
      const c = await computePresetLayers(inputs, preset, []);
      const limeUi = await limeUiPromise;
      if (get().fieldId !== fieldId) return; // the user left before this landed
      if (!c.ok || !c.soil) {
        set({
          status: "error",
          error: c.error ?? "Could not build this Rx.",
          skipped: c.skipped,
        });
        return;
      }
      if (get().fieldId !== fieldId) return;
      // Both variable-rate yield bases were resolved with the preset, from /rx-inputs.
      const yieldMaps = c.yieldMaps;
      const base: Base = {
        soil: c.soil,
        geoms: c.geoms,
        gridId: c.gridId,
        cellOrder: "grid",
        cellCount: c.cellCount,
        sources: c.sources,
        yieldMaps,
      };
      set({
        base,
        status: "ready",
        skipped: c.skipped,
        disabledToggles: c.disabled,
        yieldAvail: {
          machine: Object.keys(yieldMaps.machine),
          simulated: Object.keys(yieldMaps.simulated),
        },
      });
      // Each season lands on the best basis this field has for ITS crop (machine → simulated → the
      // per-crop flat constant), so it is never a silent zero-removal season either.
      const seeded = {
        ...get().tunables,
        ...seededBounds(nutrient, preset.rateBounds),
        ...(nutrient === "lime"
          ? {}
          : {
              seasons: resolveSeasons(
                DEFAULT_SEASON_CROPS,
                yieldMaps,
                nutrient,
              ),
            }),
      };
      const pinned = pinPhTarget(seeded.phTarget, limeUi.active);
      const opened = { ...seeded, phTarget: pinned.value };
      set({
        tunables: opened,
        opened,
        limeBufferActive: limeUi.active,
        limeBatchBanner: limeUi.banner,
        limeTargetNotice: pinned.notice,
      });
      get().set({}); // draw at the defaults
    } catch (e) {
      if (get().fieldId !== fieldId) return;
      set({
        status: "error",
        error: e instanceof Error ? e.message : "Could not build this Rx.",
      });
    }
  },

  set: (patch) => {
    const { base, tunables, albrecht, nutrient } = get();
    let next = { ...tunables, ...patch };

    const editedCritical =
      patch.pCrit !== undefined ||
      patch.kCritHighCec !== undefined ||
      patch.kCritLowCec !== undefined;
    if (editedCritical && patch.budgetLbAc === undefined) {
      // driving the critical directly retires the budget: leaving the old figure on screen beside a
      // critical it no longer describes is precisely the stale-number problem this panel keeps
      // fixing.
      next = { ...next, budgetLbAc: null };
      set({ budget: null });
    }

    if (
      base &&
      next.budgetLbAc != null &&
      (nutrient === "p2o5" || nutrient === "k2o")
    ) {
      const target = budgetTarget(next, nutrient);
      const soilNow = withTunables(
        base.soil,
        { ...next, budgetLbAc: null },
        albrecht,
        nutrient,
        base,
      );
      const sol = solveCriticalForBudget(
        {
          soil: soilNow,
          nutrient,
          // the rate window rides `soilNow` — the solver reads it there, so the two cannot disagree
          tierGap: Math.max(0, next.kCritHighCec - next.kCritLowCec),
        },
        target,
      );
      next = tunablesAfterBudget(next, nutrient, target, sol);
      set({ budget: sol });
    }

    set({ tunables: next });
    if (!base) return;
    if (
      nutrient === "lime" &&
      !albrecht &&
      get().limeBufferActive &&
      limeTargetError(next.phTarget, true)
    ) {
      return;
    }
    // Live recompute: arrays already in memory, so this is microseconds and needs no debounce.
    // Uses `assembleFertLayers` — the SAME entry point the preset path calls — so an edited Rx is
    // structurally identical to one built from a card, not a parallel construction that can drift.
    const soil = withTunables(base.soil, next, albrecht, nutrient, base);
    const { layers, summary } = assembleFertLayers(
      base.geoms,
      soil,
      optsFor(nutrient, next, base),
    );
    // Consumers that cache per layer — the extreme pins especially key on identity + feature count
    // which are IDENTICAL across edits because only the VALUES move. Without this the pins never
    // re-scan while you drag a target.
    const v = `edit-${++editSeq}`;
    set({ layers: layers.map((l) => ({ ...l, content_version: v })), summary });
  },

  // back to what the session OPENED with — the card's own configuration — not to the static defaults
  // which would drop a field-resolved rotation for one the field may have no data for
  reset: () => get().set({ ...get().opened }),

  save: async (name) => {
    const {
      fieldId,
      fieldName,
      base,
      tunables,
      albrecht,
      preset,
      nutrient,
      batchFields,
    } = get();
    if (!fieldId || !base || !preset)
      return { ok: false, error: "nothing to save" };
    if (get().saving) return { ok: false, error: "Already saving." };
    if (get().saved)
      return {
        ok: false,
        error:
          "Already saved for these fields. Close the editor and Customize again to make another.",
      };
    const others = batchFields.filter((f) => f.field_id !== fieldId);
    const spec =
      others.length > 0
        ? captureRecSpec(preset, [], name.trim() || preset.label, tunables)
        : null;
    set({ saving: true });
    try {
      if (
        nutrient === "lime" &&
        get().limeBufferActive &&
        limeTargetError(tunables.phTarget, true)
      ) {
        set({ saving: false });
        return {
          ok: false,
          error: limeTargetError(tunables.phTarget, true) ?? "Target pH",
        };
      }
      const soil = withLimeSoilStamp(
        withTunables(base.soil, tunables, albrecht, nutrient, base),
        nutrient,
      );
      const opts = optsFor(nutrient, tunables, base);
      const saved: SavedRecSet = {
        id: `frs-${new Date().getTime()}`,
        name: name.trim() || preset.label,
        createdAt: new Date().toISOString(),
        fieldId,
        ...gridStamp(base.gridId, base.cellCount),
        nutrients: [nutrient],
        // Lime is always 4 years; K covers the seasons the spread was built for — the SAME rule the preset
        // path uses, so the Action Flags agree whichever path produced the set.
        coversThroughYear:
          new Date().getFullYear() +
          (nutrient === "lime" ? 4 : Math.max(1, tunables.seasons.length)) -
          1,
        cation: false,
        sources: base.sources,
        soil,
        replacement:
          nutrient === "lime" ? null : replacementFor(tunables, base),
        kfix: nutrient === "k2o" && !!base.soil.kFixEff,
        pMin: false,
        ...(albrecht
          ? nutrient === "k2o"
            ? { albrechtK2o: true }
            : { albrechtLime: true }
          : {}),
        ...(albrecht ? { albrechtTargets: soil.albrechtTargets } : {}),
        rateWindow: "final",
      } as SavedRecSet;
      // Every field waits for the server, including a save of this field alone.
      // A rejection names the field and the server's message, and is not a saved Rx.
      let firstError: string | null = null;
      let savedSets: SavedRecSet[] | null = null;
      try {
        savedSets = await commitRecSet(saved);
      } catch (e) {
        firstError = `${fieldName || fieldId} — ${
          e instanceof Error ? e.message : "error"
        }`;
        useFertRecs.getState().fail(fieldId, firstError);
      }
      if (savedSets) {
        // Push the edited surfaces onto the map immediately, exactly as a preset run does.
        const { layers, summary } = assembleFertLayers(base.geoms, soil, opts);
        useFertRecs
          .getState()
          .ready(fieldId, layers, summary, saved.id, savedSets);
      }
      if (spec) {
        const rest = fieldFailures(await runRecBatch(others, spec));
        const failed = [firstError, rest].filter(Boolean).join("; ");
        set({ saving: false, saved: true });
        if (failed) return { ok: false, error: `Failed: ${failed}` };
      } else if (firstError) {
        set({ saving: false });
        return { ok: false, error: `Failed: ${firstError}` };
      }
      set({ saving: false });
      return { ok: true };
    } catch (e) {
      set({ saving: false });
      return {
        ok: false,
        error: e instanceof Error ? e.message : "Could not save.",
      };
    }
  },

  buildTraditionalRx: async () => {
    const { base, fieldId, nutrient, opened } = get();
    if (!base || !fieldId) return null;
    try {
      const [samples, events] = await Promise.all([
        api.fetchSoilSamples(fieldId),
        api.fetchSampleEvents(fieldId),
      ]);
      const cell = await sessionCells(fieldId, base);
      if (!cell || get().fieldId !== fieldId) return null;
      const { idwSamplePoints } = await import("../lib/checkAccuracy");
      const { krigeSampleSurface } = await import("../lib/sampleKrigeClient");
      // The same simple kriging the map underlay uses, so a traditional rec
      // is built on the surface already under the dots.
      const krig = async (prop: string): Promise<(number | null)[] | null> => {
        const pts = idwSamplePoints(samples, events, prop);
        return pts.length >= 3
          ? (await krigeSampleSurface(pts, cell.centroids)).values
          : null;
      };
      const [ph, p, k, cec] = await Promise.all([
        krig("ph"),
        krig("p"),
        krig("k"),
        krig("cec"),
      ]);
      const layer = traditionalRxLayer({
        base,
        nutrient,
        opened,
        surfaces: { ph, p, k, cec },
      });
      if (!layer || get().fieldId !== fieldId) return null;
      return layer;
    } catch {
      return null;
    }
  },

  buildRxComparison: async () => {
    const { base, fieldId, nutrient, tunables, albrecht, opened } = get();
    if (!base || !fieldId || nutrient === "lime") return null; // the cause list is P/K-specific
    try {
      const [samples, events] = await Promise.all([
        api.fetchSoilSamples(fieldId),
        api.fetchSampleEvents(fieldId),
      ]);
      const cell = await sessionCells(fieldId, base);
      if (!cell || get().fieldId !== fieldId) return null;
      const { idwSamplePoints } = await import("../lib/checkAccuracy");
      const { krigeSampleSurface } = await import("../lib/sampleKrigeClient");
      const krig = async (prop: string): Promise<(number | null)[] | null> => {
        const pts = idwSamplePoints(samples, events, prop);
        return pts.length >= 3
          ? (await krigeSampleSurface(pts, cell.centroids)).values
          : null;
      };
      const [ph, p, k, cec] = await Promise.all([
        krig("ph"),
        krig("p"),
        krig("k"),
        krig("cec"),
      ]);
      if (get().fieldId !== fieldId) return null;
      return buildRxCauseComparison({
        base,
        nutrient,
        tunables,
        opened,
        albrecht,
        surfaces: { ph, p, k, cec },
        centroids: cell.centroids,
      });
    } catch {
      return null;
    }
  },

  end: () =>
    set({
      budget: null,
      fieldId: null,
      batchFields: [],
      saved: false,
      preset: null,
      base: null,
      layers: [],
      summary: null,
      status: "idle",
      error: null,
      skipped: [],
      disabledToggles: [],
      limeBufferActive: false,
      limeBatchBanner: "",
      limeTargetNotice: null,
    }),
}));

async function resolveLimeBuffer(
  inputs: PresetInputs,
  preset: FertPreset,
  cohort: DashboardField[],
): Promise<{ active: boolean; banner: string }> {
  const buildup = preset.nutrient === "lime" && !preset.flags.limeAlbrecht;
  if (!buildup) return { active: false, banner: "" };
  if (cohort.length < 2) {
    return { active: manifestHasBufferPh(inputs.manifest), banner: "" };
  }
  const openId = inputs.field.field_id;
  const rows = await Promise.all(
    cohort.map(async (field) => {
      const manifest =
        field.field_id === openId
          ? inputs.manifest
          : await api
              .fetchFieldLayerList(field.field_id)
              .then((listed) =>
                listed.document ? layersFromDocument(listed.document) : [],
              )
              .catch(() => []);
      return {
        name: field.field_name || field.field_id,
        method: limeFieldMethod(manifestHasBufferPh(manifest), true),
      };
    }),
  );
  return {
    active: rows.some((row) => row.method === "buffer"),
    banner: limeMethodBanner(rows),
  };
}

async function sessionCells(fieldId: string, base: Base) {
  const cell = await loadRxCells(fieldId);
  if (cell.gridId !== base.gridId || cell.cellCount !== base.cellCount) {
    throw new Error(BOUNDARY_CHANGED);
  }
  return cell;
}
